/**
 * Pre-menu page — Restaurant landing with pickup-first ordering
 * Mobile-first: hero → map → order type → flow → sticky CTA
 * Desktop: two-column (order flow + sticky map)
 */

import { useMemo } from 'react'
import { MapPin, Clock, Star, ArrowRight, ExternalLink, Navigation } from 'lucide-react'
import { Button, DividerWithLabel, ThemeSwitcher } from '@/components/ui'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setOrderType, setPickupInfo } from '@/store/orderSlice'
import { PickupTimePicker, LocationPicker } from '@/components/checkout'
import { useMenuData } from '@/hooks/useMenuData'
import type { MenuLocation } from '@/hooks/useMenuData'
import { restaurantConfig } from '@/config/restaurant'
import { MapView } from '@/components/map/MapView'
import { useGeolocation } from '@/hooks/useGeolocation'
import { haversineDistance, formatDistance } from '@/utils/distance'
import { useWaitTime } from '@/hooks/useWaitTime'
import type { OrderType } from '@/types/order'

interface PreMenuPageProps {
  onNavigate: (path: string) => void
}

export function PreMenuPage({ onNavigate }: PreMenuPageProps) {
  const dispatch = useAppDispatch()
  const order = useAppSelector((state) => state.order)
  const { restaurant } = useMenuData()
  const { coords: userCoords, isGranted } = useGeolocation()
  const { waitTime } = useWaitTime()

  const restaurantName = restaurant?.title || restaurantConfig.name
  const bannerUrl = restaurant?.banner_image_url
  const logoUrl = restaurant?.logo_url
  const cuisineTags = restaurant?.cuisine_tags
  const rating = restaurant?.rating
  const reviewCount = restaurant?.review_count

  const selectedType: OrderType = order.orderType || 'pickup'

  // Multi-location support: find locations from the menu matching the order type
  const { menus } = useMenuData()
  const menuLocations: MenuLocation[] = useMemo(() => {
    // Map order type to menu channel
    const channelMap: Record<string, string> = { pickup: 'pickup', delivery: 'delivery' }
    const channel = channelMap[selectedType] || 'pickup'
    const matched = menus.find((m) => m.channel === channel)
    return matched?.locations ?? menus[0]?.locations ?? []
  }, [menus, selectedType])

  // Resolve the active location (selected or only one or none)
  const selectedLocation = useMemo<MenuLocation | null>(() => {
    if (menuLocations.length === 0) return null
    if (menuLocations.length === 1) return menuLocations[0]
    return menuLocations.find((l) => l.id === order.selectedLocationId) ?? null
  }, [menuLocations, order.selectedLocationId])

  // Coordinates: prefer selected location, fallback to config
  const lat = selectedLocation?.latitude ?? restaurantConfig.mapCoordinates.lat
  const lng = selectedLocation?.longitude ?? restaurantConfig.mapCoordinates.lng

  // Build address from selected location → restaurant API → hardcoded config
  const fullAddress = selectedLocation
    ? [selectedLocation.address, selectedLocation.city, selectedLocation.state, selectedLocation.postal_code].filter(Boolean).join(', ')
    : restaurant?.address
      ? [restaurant.address, restaurant.city, restaurant.state, restaurant.postal_code].filter(Boolean).join(', ')
      : `${restaurantConfig.address.street}, ${restaurantConfig.address.city}, ${restaurantConfig.address.state} ${restaurantConfig.address.zipCode}`

  const uberEatsUrl = selectedLocation?.uber_eats_link || restaurant?.uber_eats_link || restaurantConfig.uberEatsUrl
  const doordashUrl = selectedLocation?.doordash_link || restaurant?.doordash_link || restaurantConfig.doordashUrl
  // Phone: selectedLocation?.phone || restaurant?.phone (used in future)

  const isMultiLocation = menuLocations.length >= 2
  const needsLocationChoice = isMultiLocation && !selectedLocation

  // Build map location markers for multi-location
  const mapLocationMarkers = useMemo(() => {
    if (!isMultiLocation) return undefined
    return menuLocations
      .filter((l) => l.latitude != null && l.longitude != null)
      .map((l) => ({
        id: l.id,
        lat: l.latitude!,
        lng: l.longitude!,
        label: l.location_name,
      }))
  }, [menuLocations, isMultiLocation])

  const distanceText =
    isGranted && userCoords && !needsLocationChoice
      ? formatDistance(haversineDistance(userCoords, { lat, lng }))
      : null

  const handleOrderTypeChange = (type: OrderType) => {
    dispatch(setOrderType(type))
  }

  const handleStartOrdering = () => {
    if (selectedType === 'pickup' && !order.pickupInfo?.pickupTime) {
      dispatch(setPickupInfo({ pickupTime: 'ASAP' }))
    }
    onNavigate('/menu')
  }

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-0">
      {/* Hero — only shown when banner exists */}
      {bannerUrl && (
        <div className="relative h-[180px] sm:h-[240px] lg:h-[280px] overflow-hidden">
          <img
            src={bannerUrl}
            alt={`${restaurantName} banner`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>
      )}

      {/* Restaurant branding */}
      <div className={`px-4 sm:px-8 ${bannerUrl ? '-mt-12 sm:-mt-14 relative z-10' : 'pt-6 sm:pt-8'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2.5 sm:gap-3 mb-1">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={`${restaurantName} logo`}
                className="h-9 w-9 sm:h-12 sm:w-12 rounded-full object-cover border-2 border-background shadow-md"
              />
            )}
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight">
              {restaurantName}
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {cuisineTags && cuisineTags.length > 0 && (
              <span className="text-xs sm:text-sm text-muted-foreground">
                {cuisineTags.join(' \u00B7 ')}
              </span>
            )}
            {rating && (
              <span className="flex items-center gap-1 text-xs sm:text-sm">
                <Star className="h-3 w-3 sm:h-3.5 sm:w-3.5 fill-current text-yellow-500" />
                <span className="font-medium">{rating}</span>
                {reviewCount && (
                  <span className="text-muted-foreground">({reviewCount})</span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Mobile map — edge-to-edge, right after hero */}
      <div className="lg:hidden">
        <MapView
          restaurantCoords={{ lat, lng }}
          userCoords={userCoords}
          locations={mapLocationMarkers}
          selectedLocationId={selectedLocation?.id}
          className="w-full h-[40vh]"
        />
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 sm:py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
          {/* Left Column — Order Flow */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Location Picker — shown when menu has 2+ locations */}
            {menuLocations.length >= 2 && (
              <LocationPicker locations={menuLocations} />
            )}

            {/* Order Type Toggle */}
            <div>
              <p className="text-sm sm:text-base font-semibold mb-2 sm:mb-3">
                How do you want your order?
              </p>
              <div className="flex gap-2">
                <Button
                  variant={selectedType === 'pickup' ? 'default' : 'outline'}
                  className={`flex-1 h-12 rounded-xl font-semibold text-base transition-all ${
                    selectedType === 'pickup'
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md'
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => handleOrderTypeChange('pickup')}
                >
                  <Navigation className="mr-2 h-4 w-4" />
                  Pickup
                </Button>
                <Button
                  variant={selectedType === 'delivery' ? 'default' : 'outline'}
                  className={`flex-1 h-12 rounded-xl font-medium text-base transition-all ${
                    selectedType === 'delivery'
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md'
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => handleOrderTypeChange('delivery')}
                >
                  <MapPin className="mr-2 h-4 w-4" />
                  Delivery
                </Button>
              </div>
            </div>

            {/* Store Info — compact row (hidden when multi-location with no selection) */}
            {!needsLocationChoice && (
              <div className="flex items-center gap-3 border border-border rounded-xl px-4 py-3">
                <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{fullAddress}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 text-sm font-medium">
                  {distanceText ? (
                    <span className="text-muted-foreground">{distanceText} away</span>
                  ) : (
                    <>
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      ~{waitTime?.estimated_wait_minutes ?? 20} min
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Pickup Flow */}
            {selectedType === 'pickup' && (
              <div className="space-y-4 sm:space-y-6 animate-fade-in">
                <div className="space-y-2 sm:space-y-3">
                  <p className="text-sm sm:text-base font-semibold">
                    When do you want to pick up?
                  </p>
                  <PickupTimePicker
                    value={order.pickupInfo?.pickupTime || ''}
                    onChange={(time) =>
                      dispatch(
                        setPickupInfo({
                          ...(order.pickupInfo || { pickupTime: '' }),
                          pickupTime: time,
                        })
                      )
                    }
                  />
                </div>

                {/* Desktop CTA — hidden on mobile where sticky bar takes over */}
                <Button
                  variant="black"
                  className="hidden lg:flex w-full h-14 rounded-xl font-semibold text-base"
                  onClick={handleStartOrdering}
                >
                  Start Ordering
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            )}

            {/* Delivery Flow */}
            {selectedType === 'delivery' && (
              <div className="space-y-4 sm:space-y-5 animate-fade-in">
                <p className="text-sm text-muted-foreground">
                  We partner with delivery platforms to bring your food to you.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={uberEatsUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 h-14 rounded-xl border-2 border-border font-semibold text-base hover:border-foreground hover:bg-muted transition-all active:scale-[0.98]"
                  >
                    UberEats
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <a
                    href={doordashUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 h-14 rounded-xl border-2 border-border font-semibold text-base hover:border-foreground hover:bg-muted transition-all active:scale-[0.98]"
                  >
                    DoorDash
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>

                <DividerWithLabel label="or order pickup instead" />

                <Button
                  variant="outline"
                  className="w-full h-12 rounded-xl font-medium text-base active:scale-[0.98]"
                  onClick={() => handleOrderTypeChange('pickup')}
                >
                  Switch to Pickup
                </Button>
              </div>
            )}
          </div>

          {/* Right Column — Map (desktop only) */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="sticky top-6">
              <MapView
                restaurantCoords={{ lat, lng }}
                userCoords={userCoords}
                locations={mapLocationMarkers}
                selectedLocationId={selectedLocation?.id}
                className="w-full h-[480px] rounded-2xl overflow-hidden"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Floating theme toggle — always visible */}
      <div className="fixed top-3 right-3 z-30">
        <ThemeSwitcher />
      </div>

      {/* Mobile Sticky CTA — always visible, one thumb-tap away */}
      {selectedType === 'pickup' && (
        <div className="fixed bottom-0 left-0 right-0 lg:hidden z-20 bg-background/80 backdrop-blur-lg border-t border-border px-4 py-3 safe-area-bottom">
          <Button
            variant="black"
            className="w-full h-14 rounded-xl font-semibold text-base active:scale-[0.98]"
            onClick={handleStartOrdering}
          >
            Start Ordering
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      )}
    </div>
  )
}
